import React, { useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
} from "@mui/material";

export default function useTable(records, headCells, filterFn) {
  const pages = [15, 25, 50, 75, 100];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    pages.includes(25) ? 25 : pages[0]
  );
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const TblContainer = (props) => (
    <Table
      sx={{
        mt: 1,
        width: "100%",
        "& thead th": {
          fontWeight: 600,
          color: "secondary.main",
          backgroundColor: "primary.main",
          userSelect: "text",
          pointerEvents: "auto",
          p: 1,
          fontSize: "0.9rem",
          lineHeight: "1.2rem",
        },
        "& tbody td": {
          fontWeight: 300,
          p: "2px",
          userSelect: "text",
          pointerEvents: "auto",
        },
        "& tbody tr:hover": {
          backgroundColor: "#fffbf2",
          cursor: "pointer",
        },
      }}
    >
      {props.children}
    </Table>
  );

  const TblHead = () => {
    const handleSortRequest = (cellId) => {
      const isAsc = orderBy === cellId && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(cellId);
    };

    return (
      <TableHead>
        <TableRow>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.disableSorting ? (
                headCell.label
              ) : (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={() => handleSortRequest(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const TblPagination = () => (
    <TablePagination
      component="div"
      page={page}
      rowsPerPageOptions={pages}
      rowsPerPage={rowsPerPage}
      count={records.length}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
    />
  );

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function descendingComparator(a, b, orderBy) {
    if (orderBy === "createdAt") {
      return new Date(b[orderBy]) - new Date(a[orderBy]);
    } else if (orderBy === "staff") {
      const staffA = a.staff?.fullName?.toLowerCase() || "";
      const staffB = b.staff?.fullName?.toLowerCase() || "";
      return staffA.localeCompare(staffB);
    } else if (orderBy === "id") {
      const idA = a.orderId ? parseInt(a.orderId, 10) : a.id;
      const idB = b.orderId ? parseInt(b.orderId, 10) : b.id;
      return idA - idB;
    }
    return a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => descendingComparator(b, a, orderBy);
  }

  const recordsAfterPagingAndSorting = () =>
    stableSort(filterFn.fn(records), getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      (page + 1) * rowsPerPage
    );

  return {
    TblContainer,
    TblHead,
    TblPagination,
    recordsAfterPagingAndSorting,
  };
}
